import * as React from "react";
import { StackLayout } from "@progress/kendo-react-layout";
import { ListBox } from "@progress/kendo-react-listbox";
import {
  getNameInitials,
  useCurrentUserQuery,
  useMyAgenciesQuery,
  useUpdateMyProfileMutation,
} from "data/user/user";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { CPText } from "shared/components/base";
import useAppStore from "shared/state/useAppState";
import "./MyProfilePageStyles.scss";

const ROLE_PRIORITY = [
  "Admin",
  "Site Manager",
  "CorpUser",
  "Agency Manager / Owner",
  "Driver",
  "Tester",
  "Previewer",
];

const getTopRole = (roles: string[]): string | null => {
  for (const role of ROLE_PRIORITY) {
    if (roles.some((r) => r.toLowerCase() === role.toLowerCase())) return role;
  }
  return roles[0] ?? null;
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

interface FormState {
  firstname: string;
  lastname: string;
  email: string;
}

interface AgencyListEntry {
  code: string;
  agencyName: string;
  companyName: string;
  tier: string;
  selected: boolean;
}

const AgencyItem: React.FC<any> = ({
  dataItem,
}: {
  dataItem: AgencyListEntry;
}) => (
  <div className="mpp-agency-lb-item">
    <span className="mpp-agency-code">{dataItem.code}</span>
    <div className="mpp-agency-detail">
      <span className="mpp-agency-name">
        {dataItem.agencyName || "—"}
        {dataItem.companyName && (
          <span className="mpp-agency-company"> ({dataItem.companyName})</span>
        )}
      </span>
    </div>
    {dataItem.tier && <span className="mpp-agency-tier">{dataItem.tier}</span>}
  </div>
);

const MyProfilePage: React.FC = () => {
  const { data: user } = useCurrentUserQuery();
  const { data: myAgencies = [] } = useMyAgenciesQuery();
  const mutation = useUpdateMyProfileMutation();
  const { setToastMsg } = useAppStore();

  const [form, setForm] = React.useState<FormState>({
    firstname: "",
    lastname: "",
    email: "",
  });
  const [emailError, setEmailError] = React.useState<string | undefined>();
  const [dirty, setDirty] = React.useState(false);
  const [agencyFilter, setAgencyFilter] = React.useState("");

  React.useEffect(() => {
    if (user) {
      setForm({
        firstname: user.firstname ?? "",
        lastname: user.lastname ?? "",
        email: user.email ?? "",
      });
      setDirty(false);
    }
  }, [user]);

  const nameInitials = user ? getNameInitials(user) : "";
  const topRole = user?.roles?.length ? getTopRole(user.roles) : null;
  const isSsoUser =
    !!user?.authType && user.authType.toUpperCase() !== "TEST_ACCOUNT";

  // tier comes from the cached currentUser; name+company from the new my-agencies endpoint
  const tierMap = React.useMemo(() => {
    const m: Record<string, string> = {};
    (user?.agencies ?? []).forEach((a) => {
      m[a.code] = a.tier ?? "";
    });
    return m;
  }, [user?.agencies]);

  const allAgencyEntries = React.useMemo<AgencyListEntry[]>(
    () =>
      myAgencies.map((a) => ({
        code: a.agency,
        agencyName: a.agencyName ?? "",
        companyName: a.companyName ?? "",
        tier: tierMap[a.agency] ?? "",
        selected: false,
      })),
    [myAgencies, tierMap],
  );

  const filteredAgencies = React.useMemo(() => {
    if (!agencyFilter.trim()) return allAgencyEntries;
    const q = agencyFilter.toLowerCase();
    return allAgencyEntries.filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        a.agencyName.toLowerCase().includes(q) ||
        a.companyName.toLowerCase().includes(q),
    );
  }, [allAgencyEntries, agencyFilter]);

  const canSave =
    dirty &&
    !emailError &&
    form.firstname.trim().length > 0 &&
    form.lastname.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await mutation.mutateAsync(form);
      setToastMsg("Profile updated successfully.", "success");
      setDirty(false);
    } catch {
      setToastMsg("Failed to update profile.", "error");
    }
  };

  return (
    <div className="mpp-page">
      <div className="mpp-header">
        <CPText font="montserrat" className="mpp-title">
          My Profile
        </CPText>
      </div>

      <div className="mpp-body">
        {/* Left: avatar card */}
        <div className="mpp-avatar-card">
          <div className="mpp-avatar-circle">{nameInitials}</div>
          <CPText font="montserrat" className="mpp-display-name">
            {user?.firstname} {user?.lastname}
          </CPText>
          {topRole && <span className="mpp-role-badge">{topRole}</span>}
          <div className="mpp-meta">
            <div className="mpp-meta-item">
              <span className="mpp-meta-label">Member since</span>
              <span className="mpp-meta-value">
                {formatDate(user?.createdAt)}
              </span>
            </div>
            <div className="mpp-meta-item">
              <span className="mpp-meta-label">Last login</span>
              <span className="mpp-meta-value">
                {formatDate(user?.lastLoginAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Right: edit form + agencies */}
        <div className="mpp-right">
          <div className="mpp-section">
            <CPText font="montserrat" className="mpp-section-title">
              Personal Information
            </CPText>
            <StackLayout orientation="vertical" gap={16}>
              <StackLayout orientation="horizontal" gap={12}>
                <div style={{ flex: 1 }}>
                  <CPTextBox
                    name="firstname"
                    label="First Name"
                    value={form.firstname}
                    required
                    personNameCharacters
                    onChange={(e) => {
                      setForm((f) => ({
                        ...f,
                        firstname: e.value as string,
                      }));
                      setDirty(true);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <CPTextBox
                    name="lastname"
                    label="Last Name"
                    value={form.lastname}
                    required
                    personNameCharacters
                    onChange={(e) => {
                      setForm((f) => ({
                        ...f,
                        lastname: e.value as string,
                      }));
                      setDirty(true);
                    }}
                  />
                </div>
              </StackLayout>

              {isSsoUser ? (
                <div>
                  <CPTextBox
                    name="email"
                    label="Email Address"
                    value={form.email}
                    disabled
                    renderSuffix={() => (
                      <span
                        className="mpp-sso-lock-icon"
                        title="Managed by SSO"
                      >
                        <i className="fa-solid fa-lock" />
                      </span>
                    )}
                  />
                  <CPText
                    color="secondary"
                    style={{ display: "flex", marginTop: 4 }}
                  >
                    <i
                      className="fa-solid fa-circle-info"
                      style={{ marginRight: 5 }}
                    />
                    Your email is managed by your organization's SSO provider.
                  </CPText>
                </div>
              ) : (
                <CPEmailTextBox
                  name="email"
                  label="Email Address"
                  value={form.email}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, email: e.value as string }));
                    setEmailError(e.isValid ? undefined : e.error);
                    setDirty(true);
                  }}
                  errorMsg={emailError}
                />
              )}

              <div className="mpp-actions">
                <CPButton
                  themeColor="primary"
                  showLoader={mutation.isLoading}
                  disabled={!canSave}
                  onClick={handleSave}
                >
                  Save Changes
                </CPButton>
              </div>
            </StackLayout>
          </div>

          {allAgencyEntries.length > 0 && (
            <div className="mpp-section">
              <CPText font="montserrat" className="mpp-section-title">
                Assigned Agencies
                <span className="mpp-agency-count">
                  {filteredAgencies.length === allAgencyEntries.length
                    ? allAgencyEntries.length
                    : `${filteredAgencies.length} / ${allAgencyEntries.length}`}
                </span>
              </CPText>

              <div className="mpp-agency-filter">
                <CPTextBox
                  name="agencyFilter"
                  placeholder="Filter by code or name…"
                  value={agencyFilter}
                  onChange={(e) => setAgencyFilter(e.value as string)}
                  renderPrefix={() => (
                    <span className="mpp-agency-filter-icon">
                      <i className="fa-solid fa-magnifying-glass" />
                    </span>
                  )}
                />
              </div>

              <ListBox
                className="mpp-agency-listbox"
                data={filteredAgencies}
                textField="code"
                item={AgencyItem}
                style={{ height: 280, width: "100%" }}
              />

              {filteredAgencies.length === 0 && (
                <CPText
                  textStyle="caption"
                  color="secondary"
                  style={{ display: "block", padding: "8px 4px" }}
                >
                  No agencies match your filter.
                </CPText>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyProfilePage;
